import React, { useRef, useEffect, useCallback, useState } from 'react';
import ReactPlayer from 'react-player';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';

/**
 * Pure video player component with minimal controls and event emission
 * No telemetry or navigation logic - just video playback
 */
function VideoPlayer({
  videoUrl,
  currentTime = 0,
  isPlaying = false,
  playbackRate = 1,
  onTimeUpdate,
  onPlay,
  onPause,
  onSeek,
  onDuration,
  onReady,
  onError,
  showControls = true,
  className = '',
  style = {}
}) {
  const playerRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState({ played: 0, playedSeconds: 0 });
  const [seeking, setSeeking] = useState(false);
  const [lastEmittedTime, setLastEmittedTime] = useState(0);

  // Emit time updates at regular intervals
  const handleProgress = useCallback((progressData) => {
    setProgress(progressData);
    
    // Only emit time updates if we're not seeking and time has actually changed
    if (!seeking && Math.abs(progressData.playedSeconds - lastEmittedTime) > 0.1) {
      onTimeUpdate?.(progressData.playedSeconds, progressData);
      setLastEmittedTime(progressData.playedSeconds);
    }
  }, [seeking, onTimeUpdate, lastEmittedTime]);

  // Handle player ready
  const handleReady = useCallback(() => {
    if (playerRef.current) {
      const playerDuration = playerRef.current.getDuration();
      
      // Handle HLS streams that might report Infinity or NaN as duration
      const validDuration = (playerDuration && 
                           isFinite(playerDuration) && 
                           !isNaN(playerDuration) && 
                           playerDuration > 0) ? playerDuration : 0;
      
      setDuration(validDuration);
      onDuration?.(validDuration);
      onReady?.(playerRef.current);
      
      // For HLS streams, try to get duration again after a short delay
      if (validDuration === 0 && videoUrl?.includes('.m3u8')) {
        setTimeout(() => {
          const retryDuration = playerRef.current?.getDuration();
          if (retryDuration && isFinite(retryDuration) && !isNaN(retryDuration) && retryDuration > 0) {
            setDuration(retryDuration);
            onDuration?.(retryDuration);
          }
        }, 1000);
      }
    }
  }, [onDuration, onReady, videoUrl]);

  // Handle seeking
  const handleSeekStart = useCallback(() => {
    setSeeking(true);
  }, []);

  const handleSeekEnd = useCallback((newTime) => {
    setSeeking(false);
    if (playerRef.current && duration > 0) {
      // Clamp seek time to valid range for HLS streams
      const clampedTime = Math.max(0, Math.min(newTime, duration));
      
      // For HLS streams, use fractional seeking which is more reliable
      if (videoUrl?.includes('.m3u8')) {
        const fraction = clampedTime / duration;
        playerRef.current.seekTo(fraction, 'fraction');
      } else {
        playerRef.current.seekTo(clampedTime, 'seconds');
      }
      
      onSeek?.(clampedTime);
      setLastEmittedTime(clampedTime);
    }
  }, [onSeek, duration, videoUrl]);

  // Handle play/pause
  const handlePlay = useCallback(() => {
    onPlay?.();
  }, [onPlay]);

  const handlePause = useCallback(() => {
    onPause?.();
  }, [onPause]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  }, [isPlaying, handlePlay, handlePause]);

  // Sync external currentTime changes
  useEffect(() => {
    if (playerRef.current && !seeking && duration > 0) {
      const timeDiff = Math.abs(currentTime - progress.playedSeconds);
      
      // Only seek if there's a significant difference (1 second threshold)
      if (timeDiff > 1) {
        // For HLS streams, use fractional seeking for better accuracy
        if (videoUrl?.includes('.m3u8')) {
          const fraction = Math.max(0, Math.min(1, currentTime / duration));
          playerRef.current.seekTo(fraction, 'fraction');
        } else {
          playerRef.current.seekTo(currentTime, 'seconds');
        }
      }
    }
  }, [currentTime, seeking, progress.playedSeconds, duration, videoUrl]);

  // Format time for display
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Handle errors
  const handleError = useCallback((error) => {
    // Filter out non-critical HLS errors that don't affect playback
    if (error === 'hlsError') {
      // Check if video is still playable despite HLS error
      if (playerRef.current && !playerRef.current.getInternalPlayer()?.media?.error) {
        console.warn('HLS error occurred but video is still playable:', error);
        return; // Don't propagate non-critical HLS errors
      }
    }
    
    console.error('Video player error:', error);
    onError?.(error);
  }, [onError]);

  if (!videoUrl) {
    return (
      <Box 
        className={className}
        style={style}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 200,
          backgroundColor: 'grey.100',
          color: 'text.secondary'
        }}
      >
        <Typography variant="h6">No video available</Typography>
      </Box>
    );
  }

  return (
    <Box 
      className={className}
      style={style}
      sx={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Video Player */}
      <Box sx={{ 
        flexGrow: 1, 
        position: 'relative',
        minHeight: 0,
        backgroundColor: 'black'
      }}>
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          playing={isPlaying}
          playbackRate={playbackRate}
          onProgress={handleProgress}
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onError={handleError}
          progressInterval={100}
          width="100%"
          height="100%"
          controls={false} // We provide custom controls
          config={{
            file: {
              attributes: {
                preload: 'metadata', // Only load metadata initially, not the whole video
                crossOrigin: 'anonymous',
                playsInline: true
              },
              hlsOptions: {
                // HLS.js configuration for better seeking and error handling
                enableWorker: false,
                lowLatencyMode: false,
                backBufferLength: 90,
                maxBufferLength: 30,
                maxMaxBufferLength: 600,
                maxBufferSize: 60 * 1000 * 1000, // 60MB
                maxBufferHole: 0.5,
                nudgeOffset: 0.1,
                nudgeMaxRetry: 3,
                maxFragLookUpTolerance: 0.25,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: Infinity,
                enableSoftwareAES: true,
                manifestLoadingTimeOut: 10000,
                manifestLoadingMaxRetry: 1,
                manifestLoadingRetryDelay: 1000,
                levelLoadingTimeOut: 10000,
                levelLoadingMaxRetry: 4,
                levelLoadingRetryDelay: 1000,
                fragLoadingTimeOut: 20000,
                fragLoadingMaxRetry: 6,
                fragLoadingRetryDelay: 1000,
                startFragPrefetch: false,
                appendErrorMaxRetry: 3,
                // Enable seeking for HLS streams
                autoStartLoad: true,
                startPosition: -1,
                debug: false
              }
            },
            youtube: {
              playerVars: {
                showinfo: 0,
                controls: 0,
                rel: 0,
                modestbranding: 1
              }
            }
          }}
        />
      </Box>

      {/* Custom Controls */}
      {showControls && (
        <Box sx={{ 
          p: 1, 
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 1, px: 1 }}>
            <Slider
              value={(() => {
                if (seeking) {
                  return progress.played * 100;
                }
                
                if (duration > 0 && isFinite(duration) && progress.playedSeconds >= 0) {
                  const percentage = (progress.playedSeconds / duration) * 100;
                  return isFinite(percentage) ? Math.max(0, Math.min(100, percentage)) : 0;
                }
                
                return 0;
              })()}
              onChange={(_, value) => {
                if (duration > 0 && isFinite(duration)) {
                  const newTime = (value / 100) * duration;
                  if (isFinite(newTime) && newTime >= 0) {
                    setProgress(prev => ({
                      ...prev,
                      played: value / 100,
                      playedSeconds: newTime
                    }));
                  }
                }
              }}
              onChangeCommitted={(_, value) => {
                if (duration > 0 && isFinite(duration)) {
                  const newTime = (value / 100) * duration;
                  if (isFinite(newTime) && newTime >= 0) {
                    handleSeekEnd(newTime);
                  }
                }
              }}
              onMouseDown={handleSeekStart}
              min={0}
              max={100}
              step={0.1}
              size="small"
              sx={{
                color: 'primary.main',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                },
                '& .MuiSlider-track': {
                  height: 4,
                },
                '& .MuiSlider-rail': {
                  height: 4,
                }
              }}
            />
          </Box>

          {/* Control Bar */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between' 
          }}>
            {/* Play/Pause Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={togglePlayPause}
                size="medium"
                sx={{ color: 'primary.main' }}
              >
                {isPlaying ? (
                  <PauseCircleFilledIcon fontSize="large" />
                ) : (
                  <PlayCircleFilledIcon fontSize="large" />
                )}
              </IconButton>
            </Box>

            {/* Time Display */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatTime(progress.playedSeconds)} / {formatTime(duration)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default VideoPlayer; 